import { Controller } from '@hotwired/stimulus'
import { getReviewSurveySubjectFacilities } from '../api/common/review_survey_subject_facilities'

export default class extends Controller {
  static targets = [
    'suggest',
    'email',
    'timeToVisitContent',
    'timeToMoveInContent',
    'checkbox',
    'prefectures',
    'cities',
    'facilitiesSelect',
    'selectedFacility',
    'modal',
    'modalMain',
    'modalOpenButton',
    'fileCount',
    'uploadFile',
    'warningText',
  ]

  get className () {
    return this.element.dataset.classname
  }

  inputEmail (e) {
    if (e.target.value.slice(-1) === '@') {
      this.suggestTarget.style.display = 'block'
    }
  }

  clickSuggest (e) {
    this.emailTarget.value = this.emailTarget.value + e.target.dataset.value
    this.suggestTarget.style.display = 'none'
  }

  closeSuggest () {
    this.suggestTarget.style.display = 'none'
  }

  changeMoveInStatus (e) {
    const timeToVisitInput = this.timeToVisitContentTarget.querySelector('input')
    const timeToMoveInInput = this.timeToMoveInContentTarget.querySelector('input')

    if (e.target.value === 'visiting_only') {
      this.timeToVisitContentTarget.classList.remove(`${this.className}::hidden`)
      this.timeToMoveInContentTarget.classList.add(`${this.className}::hidden`)
      timeToMoveInInput.disabled = true
      timeToVisitInput.disabled = false
    } else {
      this.timeToMoveInContentTarget.classList.remove(`${this.className}::hidden`)
      this.timeToVisitContentTarget.classList.add(`${this.className}::hidden`)
      timeToVisitInput.disabled = true
      timeToMoveInInput.disabled = false
    }
  }

  evaluationPointsLimitExcessJudgment () {
    const limitSize = 5
    if (this.countCheckedBoxes() >= limitSize) {
      this.changeCheckboxesDisabled(true)
    } else {
      this.changeCheckboxesDisabled(false)
    }
  }

  changeCheckboxesDisabled (boolean) {
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.checked) {
        checkbox.disabled = boolean
      }
    })
  }

  countCheckedBoxes () {
    let checkedCount = 0
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkedCount++
      }
    })
    return checkedCount
  }

  checkMinimumLength (e) {
    const minimumRequiredCharacters = e.target.dataset.minimum
    const warningText = e.target.parentNode.querySelector('.warning-text')

    if (e.target.value.length < minimumRequiredCharacters) {
      warningText.style.display = 'block'
      e.target.classList.add(`${this.className}::warning-textarea`)
      e.target.classList.add('requirement-not-met')
    } else {
      warningText.style.display = 'none'
      e.target.classList.remove(`${this.className}::warning-textarea`)
      e.target.classList.remove('requirement-not-met')
    }
  }

  checkMaximumLength (e) {
    const maximumRequiredCharacters = e.target.dataset.maximum
    const warningText = e.target.parentNode.querySelector('.warning-text')

    if (e.target.value.length > maximumRequiredCharacters) {
      warningText.style.display = 'block'
      e.target.classList.add(`${this.className}::warning-textarea`)
      e.target.classList.add('requirement-not-met')
    } else {
      warningText.style.display = 'none'
      e.target.classList.remove(`${this.className}::warning-textarea`)
      e.target.classList.remove('requirement-not-met')
    }
  }

  changeRequired (e) {
    const isAllCheckboxesUnchecked = this.checkboxTargets.every(checkbox => checkbox.checked === false)

    if (e.target.checked) {
      this.checkboxTargets.forEach((checkbox) => {
        checkbox.required = false
      })
    } else {
      if (isAllCheckboxesUnchecked) {
        this.checkboxTargets.forEach((checkbox) => {
          checkbox.required = true
        })
      }
    }
  }

  get prefectureId () {
    return this.prefecturesTarget.value
  }

  get cityId () {
    return this.citiesTarget.value
  }

  changePrefecture () {
    this.citiesTarget.innerHTML = '<option value="">市区町村を選択してください</option>'
    this.resetFacilitySelection()
    if (!this.prefectureId) {
      return
    }
    fetch(`/area/prefectures/${this.prefectureId}/cities.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(cities => this.updateCities(cities))
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'review-survey', operation: 'fetch-cities' } })
        }
        console.error('Failed to fetch cities:', error)
        alert('市区町村の取得に失敗しました。ページを再読み込みしてください。')
      })
  }

  updateCities (cities) {
    this.citiesTarget.innerHTML =
      '<option value="">市区町村を選択してください</option>' +
      cities.map((city) => (
        `<option value="${city.id}">${this.escapeHtml(city.name)}</option>`
      )).join('')
  }

  // 市区町村選択後、対象施設を取得してモーダル内にカテゴリ別リストを描画する。
  // 取得成否に関わらず「施設リスト表示」ボタンを活性化する（0件・取得失敗時はモーダルに
  // 状況メッセージを出す設計のため、ボタン活性＝選択可能リストの存在を意味しない）。
  // 取得(fetch)の失敗のみを catch で握り、描画時の例外は後段の then で処理する（同じ catch に
  // 落として「施設の取得に失敗しました」と誤表示しないよう、fetch と描画の catch を分離している）。
  fetchFacilities () {
    this.resetFacilitySelection()
    if (!this.cityId) {
      return
    }

    const params = { prefecture_id: this.prefectureId, city_id: this.cityId }
    getReviewSurveySubjectFacilities(params)
      .then((data) => data.results || [])
      .catch((error) => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'review-survey', operation: 'fetch-facilities' } })
        }
        console.error('Failed to fetch facilities:', error)
        this.setModalMessage('施設の取得に失敗しました')
        if (this.hasModalOpenButtonTarget) {
          this.modalOpenButtonTarget.disabled = false
        }
        return null
      })
      .then((facilities) => {
        if (facilities === null) {
          return
        }
        this.updateFacilities(facilities)
        this.setFacilitiesToModal(facilities)
        if (this.hasModalOpenButtonTarget) {
          this.modalOpenButtonTarget.disabled = false
        }
      })
  }

  // フォーム送信・必須判定用の隠しselectのoptionを更新する
  updateFacilities (facilities) {
    this.facilitiesSelectTarget.innerHTML =
      '<option value="">施設名を選択してください</option>' +
      facilities.map((facility) => (
        `<option value="${facility.id}">${this.escapeHtml(facility.name)}</option>`
      )).join('')
  }

  toggleModal (e) {
    e.preventDefault()
    this.modalTarget.classList.toggle(`${this.className}::modal-open`)
  }

  // モーダル内にカテゴリ別の施設リスト（クリック可能な<li>）を描画する
  setFacilitiesToModal (facilities) {
    if (!this.hasModalMainTarget) {
      return
    }
    if (facilities.length === 0) {
      this.setModalMessage('対象の施設が見つかりませんでした')
      return
    }

    const groupHeading = `${this.className}::group-heading`
    const facilityList = `${this.className}::facility-list`
    const dataAction = `click->${this.identifier}#selectListFacility`
    const groupedFacilities = this.groupByFacilityCategory(facilities)

    this.modalMainTarget.innerHTML = Object.entries(groupedFacilities).map(([categoryName, categoryFacilities]) => (
      `<div class="${groupHeading}">${this.escapeHtml(categoryName)}</div>
       <ul class="${facilityList}">
         ${categoryFacilities.map(facility => (
           `<li data-facility-id="${facility.id}" data-action="${dataAction}">${this.escapeHtml(facility.name)}</li>`
         )).join('')}
       </ul>`
    )).join('')
  }

  // モーダル領域に単一メッセージ（0件・取得失敗）を表示する。target欠落時は何もしない
  setModalMessage (message) {
    if (!this.hasModalMainTarget) {
      return
    }
    this.modalMainTarget.innerHTML =
      `<p class="${this.className}::facility-list-empty">${this.escapeHtml(message)}</p>`
  }

  groupByFacilityCategory (facilities) {
    return facilities.reduce((groupedFacilities, facility) => {
      const key = facility.category_name
      if (!groupedFacilities[key]) {
        groupedFacilities[key] = []
      }
      groupedFacilities[key].push(facility)
      return groupedFacilities
    }, {})
  }

  // innerHTMLへ外部データ（施設名・市区町村名・カテゴリ名）を挿入する際のXSS対策。
  // テキストノード位置での埋め込み専用（div.textContent→innerHTMLはクォートをエスケープ
  // しないため、HTML属性値には使用しないこと）。
  escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // モーダル内の施設<li>クリックで、選択施設を確定してモーダルを閉じる
  selectListFacility (e) {
    const item = e.currentTarget
    this.facilitiesSelectTarget.value = item.dataset.facilityId

    // 選択中の施設名をページ上にテキスト表示する（textContent代入のためエスケープ不要）
    if (this.hasSelectedFacilityTarget) {
      this.selectedFacilityTarget.textContent = item.textContent
    }

    // 隠しselectのinputイベントを発火する。dispatchEvent自体は同期だが、
    // slider#changeValue側はsetTimeout(100ms)後に「次へ」活性を更新する
    this.facilitiesSelectTarget.dispatchEvent(new Event('input', { bubbles: true }))

    this.modalTarget.classList.remove(`${this.className}::modal-open`)
  }

  resetFacilitySelection () {
    if (this.hasFacilitiesSelectTarget) {
      this.facilitiesSelectTarget.innerHTML = '<option value="">施設名を選択してください</option>'
      this.facilitiesSelectTarget.value = ''
      this.facilitiesSelectTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasSelectedFacilityTarget) {
      this.selectedFacilityTarget.textContent = ''
    }
    if (this.hasModalMainTarget) {
      this.modalMainTarget.innerHTML = ''
    }
    if (this.hasModalOpenButtonTarget) {
      this.modalOpenButtonTarget.disabled = true
    }
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove(`${this.className}::modal-open`)
    }
  }

  changeFile () {
    const files = this.uploadFileTarget.files
    let fileCount = files.length
    let totalFileSize = 0

    Array.from(files).forEach((file) => {
      totalFileSize += file.size
    })
    const totalFileSizeMib = totalFileSize / 1024 / 1024

    if (totalFileSizeMib > 20) {
      alert('アップロードする画像は合計20MB以下にしてください。')
      this.uploadFileTarget.value = ''
      fileCount = 0
      this.warningTextTarget.style.display = 'block'
    } else {
      this.warningTextTarget.style.display = 'none'
    }

    this.fileCountTarget.textContent = '選択されたファイル数: ' + fileCount
    this.fileCountTarget.style.display = 'inline-block'
  }
}
